"use client"

import { useState } from "react"
import Link from "next/link"
import { Loader2, MessageSquare, Brain } from "lucide-react"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { ProseContainer, ProseBody, ProseSection } from "@/components/layout/prose-container"
import { useGoals } from "@/hooks/use-goals"
import { MemoryCitationPill } from "@/components/citations/memory-citation-pill"

type Phase = "month1" | "month2" | "month3"

// MVP outcome cards - these would come from AI/advisor in a real implementation
const MVP_OUTCOMES: Record<Phase, { title: string; rationale: string; memoryId?: string }[]> = {
  month1: [
    { title: "Define core value proposition", rationale: "Clarify what makes your offering unique" },
    { title: "Identify target customer segments", rationale: "Focus resources on highest-potential buyers" },
    { title: "Establish baseline metrics", rationale: "Measure progress from day one" },
  ],
  month2: [
    { title: "Launch pilot program", rationale: "Test assumptions with real customers" },
    { title: "Refine messaging based on feedback", rationale: "Improve conversion through iteration" },
    { title: "Build referral pipeline", rationale: "Leverage early wins for growth" },
  ],
  month3: [
    { title: "Scale winning channels", rationale: "Double down on what works" },
    { title: "Optimize unit economics", rationale: "Ensure sustainable growth path" },
    { title: "Document playbook", rationale: "Prepare for handoff or expansion" },
  ],
}

// This week's focus suggestions
const THIS_WEEK_FOCUS = [
  "Review and refine your positioning statement",
  "Schedule 3 customer discovery calls",
  "Audit current marketing spend efficiency",
]

interface OutcomeCardProps {
  title: string
  rationale: string
  memoryId?: string
}

function OutcomeCard({ title, rationale, memoryId }: OutcomeCardProps) {
  return (
    <div className="p-4 rounded-lg border border-[var(--border)] bg-[var(--surface)] hover:border-primary/30 transition-colors">
      <h4 className="text-sm font-medium text-foreground">{title}</h4>
      <p className="text-xs text-muted-foreground mt-1">{rationale}</p>
      {memoryId && (
        <div className="mt-2">
          <MemoryCitationPill label="Related memory" memoryId={memoryId} />
        </div>
      )}
    </div>
  )
}

interface ProgressChipProps {
  status: "drafted" | "in_motion" | "review"
}

function ProgressChip({ status }: ProgressChipProps) {
  const labels = {
    drafted: "Drafted",
    in_motion: "In motion",
    review: "Review",
  }

  const styles = {
    drafted: "bg-muted text-muted-foreground",
    in_motion: "bg-blue-500/10 text-blue-400 border-blue-500/20",
    review: "bg-green-500/10 text-green-400 border-green-500/20",
  }

  return (
    <span
      className={cn(
        "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border",
        styles[status]
      )}
    >
      {labels[status]}
    </span>
  )
}

export function GoalsCenterContent() {
  const { activeGoal, isLoading, relatedMemories, progressStatus } = useGoals()
  const [selectedPhase, setSelectedPhase] = useState<Phase>("month1")

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  // Empty state: no goal set
  if (!activeGoal) {
    return (
      <ProseContainer>
        <p className="font-serif text-[18px] text-muted-foreground leading-relaxed -mt-4">
          Your 90-day strategic focus
        </p>

        <ProseSection>
          <ProseBody>
            Goals give your advisor a clear lens for evaluating opportunities, tradeoffs, and risks. Setting a goal unlocks strategic, opinionated advice tailored to your specific ambition.
          </ProseBody>
        </ProseSection>

        {/* 90-day timeline placeholder */}
        <div className="space-y-4 pt-4">
          <div className="flex items-center justify-between border-t border-b border-[var(--border)] py-6">
            <div className="flex-1 text-center">
              <div className="text-xs font-sans font-medium text-muted-foreground uppercase tracking-wide">
                Month 1
              </div>
            </div>
            <div className="w-px h-8 bg-[var(--border)]" />
            <div className="flex-1 text-center">
              <div className="text-xs font-sans font-medium text-muted-foreground uppercase tracking-wide">
                Month 2
              </div>
            </div>
            <div className="w-px h-8 bg-[var(--border)]" />
            <div className="flex-1 text-center">
              <div className="text-xs font-sans font-medium text-muted-foreground uppercase tracking-wide">
                Month 3
              </div>
            </div>
          </div>
          <p className="text-xs font-sans text-muted-foreground text-center">
            Your roadmap will appear here once a goal is set
          </p>
        </div>

        {/* CTA */}
        <div className="pt-6 space-y-2 text-center">
          <Button variant="outline" size="lg" className="px-6 bg-transparent font-sans" asChild>
            <Link href="/advisor?new=1&from=goals&scope=90_day_goal">Set my first goal</Link>
          </Button>
          <p className="text-xs font-sans text-muted-foreground">
            Via onboarding or by chatting with your Advisor
          </p>
        </div>
      </ProseContainer>
    )
  }

  // Active goal state: Planning Overview
  return (
    <ProseContainer className="space-y-6">
      {/* A. Header block */}
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <h2 className="text-[24px] font-display font-normal text-foreground leading-[1.33]">
            {activeGoal.title}
          </h2>
          <ProgressChip status={progressStatus} />
        </div>
        <p className="font-serif text-[16px] text-muted-foreground">
          A 90-day plan built around outcomes, not tasks.
        </p>
      </div>

      {/* B. Timeline component - 3-phase / 3-month tabs */}
      <div className="space-y-4">
        {/* Phase tabs */}
        <div className="flex border-b border-[var(--border)]">
          <button
            onClick={() => setSelectedPhase("month1")}
            className={cn(
              "flex-1 py-3 text-sm font-sans font-medium text-center transition-colors",
              "border-b-2 -mb-px",
              selectedPhase === "month1"
                ? "border-primary text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground"
            )}
          >
            Month 1
            <span className="block text-xs font-normal text-muted-foreground">Foundation</span>
          </button>
          <button
            onClick={() => setSelectedPhase("month2")}
            className={cn(
              "flex-1 py-3 text-sm font-sans font-medium text-center transition-colors",
              "border-b-2 -mb-px",
              selectedPhase === "month2"
                ? "border-primary text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground"
            )}
          >
            Month 2
            <span className="block text-xs font-normal text-muted-foreground">Execution</span>
          </button>
          <button
            onClick={() => setSelectedPhase("month3")}
            className={cn(
              "flex-1 py-3 text-sm font-sans font-medium text-center transition-colors",
              "border-b-2 -mb-px",
              selectedPhase === "month3"
                ? "border-primary text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground"
            )}
          >
            Month 3
            <span className="block text-xs font-normal text-muted-foreground">Scale</span>
          </button>
        </div>

        {/* Outcome cards for selected phase */}
        <div className="space-y-3">
          {MVP_OUTCOMES[selectedPhase].map((outcome, idx) => {
            // Link a related memory if available
            const linkedMemory = relatedMemories[idx]
            return (
              <OutcomeCard
                key={`${selectedPhase}-${idx}`}
                title={outcome.title}
                rationale={outcome.rationale}
                memoryId={linkedMemory?.id}
              />
            )
          })}
        </div>

        {/* Memory citations row */}
        {relatedMemories.length > 0 && (
          <div className="flex flex-wrap gap-2 pt-2">
            {relatedMemories.slice(0, 3).map((memory) => (
              <MemoryCitationPill
                key={memory.id}
                label={memory.title}
                memoryId={memory.id}
                domain={memory.domain}
              />
            ))}
            {relatedMemories.length > 3 && (
              <Link
                href="/brain"
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs text-muted-foreground hover:text-foreground"
              >
                <Brain className="h-3 w-3" />
                +{relatedMemories.length - 3} more
              </Link>
            )}
          </div>
        )}
      </div>

      {/* C. This Week Focus (advice-only) */}
      <div className="p-4 rounded-lg border border-[var(--border)] bg-[var(--surface-context)]">
        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
          This Week's Focus
        </h3>
        <div className="space-y-2">
          {THIS_WEEK_FOCUS.map((focus, idx) => (
            <Link
              key={idx}
              href={`/advisor?new=1&prompt=${encodeURIComponent(focus)}`}
              className={cn(
                "flex items-center justify-between p-3 rounded-md",
                "bg-[var(--surface)] hover:bg-[var(--surface-hover)]",
                "border border-[var(--border)] hover:border-primary/30",
                "transition-colors group"
              )}
            >
              <span className="text-sm text-foreground">{focus}</span>
              <span className="flex items-center gap-1 text-xs text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                <MessageSquare className="h-3 w-3" />
                Discuss
              </span>
            </Link>
          ))}
        </div>
      </div>

      {/* Talk with Advisor CTA */}
      <div className="text-center pt-2">
        <Link
          href="/advisor?new=1&from=goals&scope=planning"
          className="inline-flex items-center gap-2 text-sm font-sans text-muted-foreground hover:text-foreground transition-colors"
        >
          <MessageSquare className="h-4 w-4" />
          Discuss this plan with your Advisor
        </Link>
      </div>
    </ProseContainer>
  )
}
